import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { BookMarked, Loader2, RefreshCw, Trash2 } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const KIND = { repoint: { label: 're-pointed', cls: 'border-[#4ABFCF]/60 bg-[#4ABFCF]/5 text-[#0F2040]' }, confirm: { label: 'confirmed', cls: 'border-emerald-200 bg-emerald-50 text-emerald-700' } };

/** Admin — every client's remembered portal re-points / confirmed fuzzy matches; prune a wrong one without opening a project. */
export const PortalMappingsPanel = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState('');
  const load = async () => {
    try { setData((await axios.get(`${API_URL}/api/rfp/admin/portal-mappings`, { withCredentials: true })).data); }
    catch (e) { setData({ clients: [], total: 0, error: e?.response?.data?.detail || 'Could not load' }); }
  };
  useEffect(() => { load(); }, []);
  const forget = async (id, clientKey, label) => {
    if (id === 'all' && !window.confirm(`Forget every remembered portal mapping for ${label}?`)) return;
    setBusy(id === 'all' ? clientKey : id);
    try {
      const r = await axios.delete(`${API_URL}/api/rfp/admin/portal-mappings/${id}`, { params: id === 'all' ? { client_key: clientKey } : {}, withCredentials: true });
      toast.success(`Forgot ${r.data.deleted} mapping${r.data.deleted === 1 ? '' : 's'} — that wording is matched afresh next round-trip`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not forget'); }
    finally { setBusy(''); }
  };
  return (
    <Card data-testid="admin-portal-mappings">
      <CardContent className="p-5 space-y-3">
        <div className="flex items-center gap-2">
          <BookMarked className="w-4 h-4 text-[#4ABFCF]" />
          <h3 className="font-semibold text-[#0F2040]">Remembered portal mappings</h3>
          <span className="text-[10px] text-slate-500">Every re-point or confirmed fuzzy match in a portal round-trip is kept per client — the same portal wording then maps straight to that row (by its text) and never lands in the review queue again.</span>
          <Button size="sm" variant="ghost" className="ml-auto h-7 text-xs" onClick={load} data-testid="admin-portal-mappings-refresh"><RefreshCw className="w-3 h-3 mr-1" />Refresh</Button>
        </div>
        {!data && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
        {data?.error && <p className="text-xs text-rose-600">{data.error}</p>}
        {data && !data.error && !data.total && <p className="text-xs text-slate-400" data-testid="admin-portal-mappings-empty">Nothing remembered yet — decisions appear after the first fuzzy review in a portal round-trip.</p>}
        {data?.clients.map((g) => (
          <div key={g.client_key} className="border border-slate-200 rounded-md" data-testid={`admin-portal-mappings-client-${g.client_key.replace(/\s+/g, '-')}`}>
            <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-50 text-xs">
              <span className="font-semibold text-[#0F2040]">{g.client}</span>
              <span className="text-slate-400">{g.items.length} remembered</span>
              <button type="button" className="ml-auto text-[10px] text-rose-500 hover:text-rose-700 inline-flex items-center gap-1 disabled:opacity-50" disabled={busy === g.client_key} onClick={() => forget('all', g.client_key, g.client)} data-testid={`admin-portal-mappings-clear-${g.client_key.replace(/\s+/g, '-')}`}><Trash2 className="w-3 h-3" />forget all</button>
            </div>
            <table className="w-full text-[11px]">
              <thead className="text-slate-400 text-left"><tr><th className="px-3 py-1 font-medium">Portal wording</th><th className="py-1 font-medium">→ Our row</th><th className="py-1 font-medium">How</th><th className="py-1 font-medium">Taught in</th><th className="py-1 font-medium">By</th><th className="py-1" /></tr></thead>
              <tbody>
                {g.items.map((m, i) => (
                  <tr key={m.id} className="border-t border-slate-100 align-top" data-testid={`admin-portal-mapping-${g.client_key.replace(/\s+/g, '-')}-${i}`}>
                    <td className="px-3 py-1 text-slate-700">{m.portal_question}</td>
                    <td className="py-1 text-[#0F2040]"><span className="font-mono text-slate-400 mr-1">{m.target_no}</span>{m.target_text}</td>
                    <td className="py-1"><span className={`rounded-full border px-1.5 text-[9px] ${(KIND[m.kind] || KIND.repoint).cls}`}>{(KIND[m.kind] || KIND.repoint).label}</span></td>
                    <td className="py-1 text-slate-500 whitespace-nowrap">{m.project_name}<span className="block text-slate-400">{m.at?.slice(0, 10)}</span></td>
                    <td className="py-1 text-slate-500">{m.by}</td>
                    <td className="py-1 pr-3 text-right"><button type="button" className="text-slate-400 hover:text-rose-600 disabled:opacity-50" disabled={busy === m.id} onClick={() => forget(m.id, g.client_key, g.client)} title="Forget this mapping" data-testid={`admin-portal-mapping-forget-${g.client_key.replace(/\s+/g, '-')}-${i}`}><Trash2 className="w-3.5 h-3.5" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};
